import { DataFactory, type Store, type Term } from "n3";
import type { PrefixTable } from "./prefixes";
import { CLASS_TYPE_IRIS, PROPERTY_TYPE_IRIS, VOCAB } from "./vocab";
import type {
  DatasetDescriptor,
  PropertyRow,
  PropertyShapeConstraint,
  ShapeInfo,
  TermDetail,
  TermKind,
  TermSummary,
} from "./types";

const { namedNode } = DataFactory;

export interface ModelContext {
  store: Store;
  prefixes: PrefixTable;
  datasetIdByGraphIri: Map<string, string>;
  /** Which version of each dataset is loaded in `store`. */
  versionIdByDatasetId: Map<string, string>;
  datasets: DatasetDescriptor[];
}

const STRUCTURAL_TYPE_IRIS = new Set<string>([
  ...CLASS_TYPE_IRIS,
  ...PROPERTY_TYPE_IRIS,
  VOCAB.sh.NodeShape,
  VOCAB.sh.PropertyShape,
  VOCAB.owl.Ontology,
  VOCAB.rdfs.Datatype,
]);

// ---------------------------------------------------------------------------
// Low-level store helpers
// ---------------------------------------------------------------------------

function isNamedNode(term: Term): boolean {
  return term.termType === "NamedNode";
}

function getTypeIris(store: Store, iri: string): string[] {
  return store
    .getObjects(namedNode(iri), namedNode(VOCAB.rdf.type), null)
    .filter(isNamedNode)
    .map((t) => t.value);
}

function getFirstLiteral(
  store: Store,
  iri: string,
  predicateIri: string,
  preferredLang = "en",
): string | null {
  const literals = store
    .getObjects(namedNode(iri), namedNode(predicateIri), null)
    .filter((t) => t.termType === "Literal");
  if (literals.length === 0) return null;
  const preferred = literals.find(
    (l) => "language" in l && l.language === preferredLang,
  );
  const unlocalized = literals.find(
    (l) => "language" in l && l.language === "",
  );
  return (preferred ?? unlocalized ?? literals[0])!.value;
}

const SKOS_PREF_LABEL = "http://www.w3.org/2004/02/skos/core#prefLabel";
const SKOS_DEFINITION = "http://www.w3.org/2004/02/skos/core#definition";
const DCTERMS_DESCRIPTION = "http://purl.org/dc/terms/description";
/** IAO "definition" annotation — how OBO-family ontologies (e.g. BFO) attach textual definitions. */
const IAO_DEFINITION = "http://purl.obolibrary.org/obo/IAO_0000115";

/** Human label with vocabulary-appropriate fallbacks: rdfs:label, then skos:prefLabel. */
function getLabel(store: Store, iri: string): string | null {
  return (
    getFirstLiteral(store, iri, VOCAB.rdfs.label) ??
    getFirstLiteral(store, iri, SKOS_PREF_LABEL)
  );
}

/**
 * Textual definition with vocabulary-appropriate fallbacks — different
 * ontology families attach prose to different annotation properties
 * (rdfs:comment for W3C vocabularies, skos:definition for SKOS-styled ones,
 * IAO_0000115 for OBO/BFO, dcterms:description as a last resort).
 */
function getDefinition(store: Store, iri: string): string | null {
  return (
    getFirstLiteral(store, iri, VOCAB.rdfs.comment) ??
    getFirstLiteral(store, iri, SKOS_DEFINITION) ??
    getFirstLiteral(store, iri, IAO_DEFINITION) ??
    getFirstLiteral(store, iri, DCTERMS_DESCRIPTION)
  );
}

function classifyKind(typeIris: string[]): TermKind {
  if (typeIris.includes(VOCAB.sh.NodeShape)) return "NodeShape";
  if (typeIris.includes(VOCAB.sh.PropertyShape)) return "PropertyShape";
  if (typeIris.includes(VOCAB.owl.Ontology)) return "Ontology";
  if (typeIris.some((t) => CLASS_TYPE_IRIS.includes(t))) return "Class";
  if (typeIris.includes(VOCAB.owl.ObjectProperty)) return "ObjectProperty";
  if (typeIris.includes(VOCAB.owl.DatatypeProperty)) return "DatatypeProperty";
  if (typeIris.includes(VOCAB.owl.AnnotationProperty))
    return "AnnotationProperty";
  if (typeIris.includes(VOCAB.rdf.Property)) return "Property";
  if (typeIris.includes(VOCAB.rdfs.Datatype)) return "Datatype";
  return "Individual";
}

function getDatasetId(ctx: ModelContext, iri: string): string {
  for (const quad of ctx.store.getQuads(namedNode(iri), null, null, null)) {
    const datasetId = ctx.datasetIdByGraphIri.get(quad.graph.value);
    if (datasetId) return datasetId;
  }
  return "unknown";
}

function byLabel(a: TermSummary, b: TermSummary): number {
  return (a.label ?? a.prefixedName).localeCompare(b.label ?? b.prefixedName);
}

function dedupeByIri<T extends { iri: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.iri)) return false;
    seen.add(item.iri);
    return true;
  });
}

// ---------------------------------------------------------------------------
// Term summaries
// ---------------------------------------------------------------------------

export function buildSummary(ctx: ModelContext, iri: string): TermSummary {
  const typeIris = getTypeIris(ctx.store, iri);
  return {
    iri,
    prefixedName: ctx.prefixes.compact(iri),
    label: getLabel(ctx.store, iri),
    comment: getDefinition(ctx.store, iri),
    kind: classifyKind(typeIris),
    datasetId: getDatasetId(ctx, iri),
  };
}

function listSubjectsOfType(ctx: ModelContext, typeIris: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const typeIri of typeIris) {
    for (const subject of ctx.store.getSubjects(
      VOCAB.rdf.type,
      typeIri,
      null,
    )) {
      if (subject.termType === "NamedNode") seen.add(subject.value);
    }
  }
  return [...seen];
}

export function listClasses(ctx: ModelContext): TermSummary[] {
  return listSubjectsOfType(ctx, CLASS_TYPE_IRIS)
    .map((iri) => buildSummary(ctx, iri))
    .sort(byLabel);
}

export function listProperties(ctx: ModelContext): TermSummary[] {
  return listSubjectsOfType(ctx, PROPERTY_TYPE_IRIS)
    .map((iri) => buildSummary(ctx, iri))
    .sort(byLabel);
}

export function listNodeShapes(ctx: ModelContext): TermSummary[] {
  return listSubjectsOfType(ctx, [VOCAB.sh.NodeShape])
    .map((iri) => buildSummary(ctx, iri))
    .sort(byLabel);
}

export function listOntologies(ctx: ModelContext): TermSummary[] {
  return listSubjectsOfType(ctx, [VOCAB.owl.Ontology])
    .map((iri) => buildSummary(ctx, iri))
    .sort(byLabel);
}

/** Named individuals — instances of application classes rather than of the meta-vocabulary itself (e.g. a QUDT sample unit, a SAREF4ENER individual). */
export function listIndividuals(ctx: ModelContext): TermSummary[] {
  const seen = new Set<string>();
  const results: TermSummary[] = [];
  for (const quad of ctx.store.getQuads(
    null,
    namedNode(VOCAB.rdf.type),
    null,
    null,
  )) {
    if (quad.subject.termType !== "NamedNode") continue;
    if (quad.object.termType !== "NamedNode") continue;
    if (STRUCTURAL_TYPE_IRIS.has(quad.object.value)) continue;
    if (seen.has(quad.subject.value)) continue;
    seen.add(quad.subject.value);
    results.push(buildSummary(ctx, quad.subject.value));
  }
  return results.sort(byLabel);
}

/** Every browsable/searchable term across all tabs, deduplicated — backs the Fuse.js search index. */
export function listAllTerms(ctx: ModelContext): TermSummary[] {
  return dedupeByIri([
    ...listClasses(ctx),
    ...listProperties(ctx),
    ...listNodeShapes(ctx),
    ...listOntologies(ctx),
    ...listIndividuals(ctx),
  ]);
}

export function countTermsByDataset(ctx: ModelContext): Map<string, number> {
  const counts = new Map<string, number>();
  for (const term of listAllTerms(ctx)) {
    counts.set(term.datasetId, (counts.get(term.datasetId) ?? 0) + 1);
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Relationship lookups
// ---------------------------------------------------------------------------

function getObjectSummaries(
  ctx: ModelContext,
  subjectIri: string,
  predicateIri: string,
): TermSummary[] {
  return dedupeByIri(
    ctx.store
      .getObjects(namedNode(subjectIri), namedNode(predicateIri), null)
      .filter(isNamedNode)
      .map((term) => buildSummary(ctx, term.value)),
  ).sort(byLabel);
}

function getSubjectSummaries(
  ctx: ModelContext,
  predicateIri: string,
  objectIri: string,
): TermSummary[] {
  return dedupeByIri(
    ctx.store
      .getSubjects(namedNode(predicateIri), namedNode(objectIri), null)
      .filter(isNamedNode)
      .map((term) => buildSummary(ctx, term.value)),
  ).sort(byLabel);
}

// ---------------------------------------------------------------------------
// Generic property table
// ---------------------------------------------------------------------------

const PROPERTY_TABLE_EXCLUDED_PREDICATES = new Set<string>([
  VOCAB.rdfs.label,
  VOCAB.rdfs.comment,
]);

const XSD_STRING_IRI = "http://www.w3.org/2001/XMLSchema#string";

/**
 * OWL restrictions are anonymous (blank-node) class expressions — e.g.
 * `saref:Device rdfs:subClassOf [ a owl:Restriction ; owl:onProperty
 * saref:hasFunction ; owl:someValuesFrom saref:Function ]`. They're
 * extremely common in the bundled ontologies (SAREF, BFO, QUDT all lean on
 * them heavily), so rendering them as a bare "(blank node)" in the property
 * table would bury genuinely useful axioms behind a wall of noise. This
 * reconstructs a short, readable summary instead — not a full Manchester
 * Syntax renderer, just the restriction kinds actually present in this
 * app's bundled data.
 */
function formatRestriction(ctx: ModelContext, restrictionNode: Term): string | null {
  const typeIris = ctx.store
    .getObjects(restrictionNode, namedNode(VOCAB.rdf.type), null)
    .map((t) => t.value);
  if (!typeIris.includes(VOCAB.owl.Restriction)) return null;

  const getFirst = (predicateIri: string): Term | undefined =>
    ctx.store.getObjects(restrictionNode, namedNode(predicateIri), null)[0];

  const onProperty = getFirst(VOCAB.owl.onProperty);
  const propertyLabel = onProperty ? formatObjectValue(ctx, onProperty) : "?";

  const someValuesFrom = getFirst(VOCAB.owl.someValuesFrom);
  if (someValuesFrom) return `∃ ${propertyLabel}.${formatObjectValue(ctx, someValuesFrom)}`;

  const allValuesFrom = getFirst(VOCAB.owl.allValuesFrom);
  if (allValuesFrom) return `∀ ${propertyLabel}.${formatObjectValue(ctx, allValuesFrom)}`;

  const hasValue = getFirst(VOCAB.owl.hasValue);
  if (hasValue) return `${propertyLabel} = ${formatObjectValue(ctx, hasValue)}`;

  const onClass = getFirst(VOCAB.owl.onClass);
  const qualifiedCardinality = getFirst(VOCAB.owl.qualifiedCardinality);
  const minQualified = getFirst(VOCAB.owl.minQualifiedCardinality);
  const maxQualified = getFirst(VOCAB.owl.maxQualifiedCardinality);
  const cardinality = getFirst(VOCAB.owl.cardinality);
  const minCardinality = getFirst(VOCAB.owl.minCardinality);
  const maxCardinality = getFirst(VOCAB.owl.maxCardinality);
  const classSuffix = onClass ? `.${formatObjectValue(ctx, onClass)}` : "";

  if (qualifiedCardinality) return `${propertyLabel} =${qualifiedCardinality.value}${classSuffix}`;
  if (cardinality) return `${propertyLabel} =${cardinality.value}`;
  if (minQualified || maxQualified) {
    return `${propertyLabel} ${minQualified ? `≥${minQualified.value}` : ""}${
      minQualified && maxQualified ? ", " : ""
    }${maxQualified ? `≤${maxQualified.value}` : ""}${classSuffix}`;
  }
  if (minCardinality || maxCardinality) {
    return `${propertyLabel} ${minCardinality ? `≥${minCardinality.value}` : ""}${
      minCardinality && maxCardinality ? ", " : ""
    }${maxCardinality ? `≤${maxCardinality.value}` : ""}`;
  }

  return `owl:Restriction on ${propertyLabel}`;
}

/**
 * `sh:property [ sh:path ...; sh:minCount ...; ... ]` blank nodes are the
 * other extremely common anonymous-node shape in this app's data (every
 * NodeShape in example-shapes.ttl uses them). Inline property shapes are
 * conventionally untyped — SHACL doesn't require `a sh:PropertyShape` on
 * them — so detection goes by the presence of `sh:path` rather than
 * `rdf:type`, matching how validators actually treat them.
 */
function formatShaclPropertyShape(ctx: ModelContext, propertyShapeNode: Term): string | null {
  const hasPath = ctx.store.getObjects(propertyShapeNode, namedNode(VOCAB.sh.path), null).length > 0;
  if (!hasPath) return null;

  const constraint = buildPropertyShapeConstraint(ctx, propertyShapeNode);
  const pathLabel = constraint.pathPrefixed ?? "?";
  const typeLabel = constraint.classPrefixed ?? constraint.datatypePrefixed;
  const min = constraint.minCount ?? 0;
  const max = constraint.maxCount ?? "*";
  return `${pathLabel}${typeLabel ? ` (${typeLabel})` : ""} [${min}..${max}]`;
}

function formatObjectValue(ctx: ModelContext, term: Term): string {
  if (term.termType === "NamedNode") return ctx.prefixes.compact(term.value);
  if (term.termType === "BlankNode") {
    return (
      formatRestriction(ctx, term) ??
      formatShaclPropertyShape(ctx, term) ??
      "(complex class expression)"
    );
  }
  if (term.termType === "Literal") {
    if (term.language) return `"${term.value}"@${term.language}`;
    if (term.datatype && term.datatype.value !== XSD_STRING_IRI) {
      return `"${term.value}"^^${ctx.prefixes.compact(term.datatype.value)}`;
    }
    return term.value;
  }
  return term.value;
}

function buildPropertyRows(ctx: ModelContext, iri: string): PropertyRow[] {
  return ctx.store
    .getQuads(namedNode(iri), null, null, null)
    .filter((quad) => !PROPERTY_TABLE_EXCLUDED_PREDICATES.has(quad.predicate.value))
    .map((quad) => ({
      predicateIri: quad.predicate.value,
      predicatePrefixed: ctx.prefixes.compact(quad.predicate.value),
      predicateLabel: getFirstLiteral(ctx.store, quad.predicate.value, VOCAB.rdfs.label),
      value: formatObjectValue(ctx, quad.object),
      valueIri: quad.object.termType === "NamedNode" ? quad.object.value : null,
    }));
}

// ---------------------------------------------------------------------------
// SHACL shapes
// ---------------------------------------------------------------------------

function buildPropertyShapeConstraint(
  ctx: ModelContext,
  propertyShapeNode: Term,
): PropertyShapeConstraint {
  const pathTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.path),
    null,
  )[0];
  const classTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.class),
    null,
  )[0];
  const datatypeTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.datatype),
    null,
  )[0];
  const nodeTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.node),
    null,
  )[0];
  const minCountTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.minCount),
    null,
  )[0];
  const maxCountTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.maxCount),
    null,
  )[0];
  const patternTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.pattern),
    null,
  )[0];
  const nameTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.name),
    null,
  )[0];
  const descriptionTerm = ctx.store.getObjects(
    propertyShapeNode,
    namedNode(VOCAB.sh.description),
    null,
  )[0];

  const pathIri = pathTerm && pathTerm.termType === "NamedNode" ? pathTerm.value : null;

  return {
    pathIri,
    pathPrefixed: pathIri ? ctx.prefixes.compact(pathIri) : null,
    name: nameTerm?.value ?? null,
    description: descriptionTerm?.value ?? null,
    classIri: classTerm && classTerm.termType === "NamedNode" ? classTerm.value : null,
    classPrefixed:
      classTerm && classTerm.termType === "NamedNode"
        ? ctx.prefixes.compact(classTerm.value)
        : null,
    datatypeIri:
      datatypeTerm && datatypeTerm.termType === "NamedNode" ? datatypeTerm.value : null,
    datatypePrefixed:
      datatypeTerm && datatypeTerm.termType === "NamedNode"
        ? ctx.prefixes.compact(datatypeTerm.value)
        : null,
    minCount: minCountTerm ? Number.parseInt(minCountTerm.value, 10) : null,
    maxCount: maxCountTerm ? Number.parseInt(maxCountTerm.value, 10) : null,
    pattern: patternTerm?.value ?? null,
    nodeIri: nodeTerm && nodeTerm.termType === "NamedNode" ? nodeTerm.value : null,
  };
}

function buildShapeInfo(ctx: ModelContext, shapeIri: string): ShapeInfo {
  const targetClassIris = ctx.store
    .getObjects(namedNode(shapeIri), namedNode(VOCAB.sh.targetClass), null)
    .filter(isNamedNode)
    .map((t) => t.value);

  const properties = ctx.store
    .getObjects(namedNode(shapeIri), namedNode(VOCAB.sh.property), null)
    .map((propertyShapeNode) =>
      buildPropertyShapeConstraint(ctx, propertyShapeNode),
    );

  return {
    shapeIri,
    shapePrefixed: ctx.prefixes.compact(shapeIri),
    targetClassIris,
    properties,
  };
}

// ---------------------------------------------------------------------------
// Full term detail
// ---------------------------------------------------------------------------

export function getTermDetail(ctx: ModelContext, iri: string): TermDetail {
  const typeIris = getTypeIris(ctx.store, iri);
  const summary = buildSummary(ctx, iri);

  const equivalentClasses = dedupeByIri([
    ...getObjectSummaries(ctx, iri, VOCAB.owl.equivalentClass),
    ...getSubjectSummaries(ctx, VOCAB.owl.equivalentClass, iri),
  ]);

  const shapes = typeIris.includes(VOCAB.sh.NodeShape)
    ? [buildShapeInfo(ctx, iri)]
    : [];

  const targetedByShapes = ctx.store
    .getSubjects(VOCAB.sh.targetClass, namedNode(iri), null)
    .filter(isNamedNode)
    .map((s) => buildShapeInfo(ctx, s.value));

  const instances = ctx.store
    .getSubjects(VOCAB.rdf.type, namedNode(iri), null)
    .filter(isNamedNode)
    .map((s) => buildSummary(ctx, s.value))
    .sort(byLabel);

  return {
    ...summary,
    typeIris,
    superClasses: getObjectSummaries(ctx, iri, VOCAB.rdfs.subClassOf),
    subClasses: getSubjectSummaries(ctx, VOCAB.rdfs.subClassOf, iri),
    superProperties: getObjectSummaries(ctx, iri, VOCAB.rdfs.subPropertyOf),
    subProperties: getSubjectSummaries(ctx, VOCAB.rdfs.subPropertyOf, iri),
    domain: getObjectSummaries(ctx, iri, VOCAB.rdfs.domain),
    range: getObjectSummaries(ctx, iri, VOCAB.rdfs.range),
    equivalentClasses,
    properties: buildPropertyRows(ctx, iri),
    shapes,
    targetedByShapes,
    instances,
  };
}

export function termExists(ctx: ModelContext, iri: string): boolean {
  return ctx.store.countQuads(namedNode(iri), null, null, null) > 0;
}
